import { useCallback } from 'react';

import { useFlowContext } from '../contexts/FlowContext';

/**
 * Hook for direct access to the flow engine instance
 * Use this when you need full control over the engine
 */
export const useFlowEngine = () => {
  const { engine } = useFlowContext();
  return engine;
};

/**
 * Hook for accessing flow state and current step information
 * Use this for read-only access to flow state
 */
export const useFlowState = () => {
  const { 
    flowState, 
    currentStep, 
    isFlowActive, 
    isLoading,
    inputVisibility
  } = useFlowContext();

  return {
    flowState,
    currentStep,
    isFlowActive,
    isLoading,
    inputVisibility,
    // Computed properties for convenience
    flowId: flowState?.flow?.id,
    stepId: currentStep?.id,
    stepType: currentStep?.type,
    variables: flowState?.variables || {},
  };
};

/**
 * Hook for flow control actions
 * Use this for triggering flow operations
 */
export const useFlowActions = () => {
  const { 
    loadFlow, 
    startFlow, 
    stopFlow, 
    nextStep, 
    selectChip,
    setVariable, 
    getVariable 
  } = useFlowContext();

  const loadAndStart = useCallback(async (flowId: string, variables?: Record<string, any>) => {
    await loadFlow(flowId, variables);
    startFlow();
  }, [loadFlow, startFlow]);

  const submitUserInput = useCallback(async (input: any) => {
    await nextStep(input);
  }, [nextStep]);

  const updateVariable = useCallback((key: string, value: any) => {
    setVariable(key, value);
  }, [setVariable]);

  const readVariable = useCallback((key: string) => {
    return getVariable(key);
  }, [getVariable]);

  return {
    loadFlow,
    startFlow,
    stopFlow,
    nextStep,
    selectChip,
    loadAndStart,
    submitUserInput,
    setVariable: updateVariable,
    getVariable: readVariable,
  };
};

/**
 * Hook for registering flow event listeners
 * Use this to react to flow events in components
 */
export const useFlowEvents = () => {
  const { engine } = useFlowContext();

  const on = useCallback((event: string, handler: (...args: any[]) => void) => {
    if (!engine) return;
    engine.on(event as any, handler);
    
    // Return cleanup function
    return () => {
      engine.off(event as any, handler);
    };
  }, [engine]);

  const off = useCallback((event: string, handler: (...args: any[]) => void) => {
    if (!engine) return;
    engine.off(event as any, handler);
  }, [engine]);

  const emit = useCallback((event: string, data?: any) => {
    if (!engine) return;
    engine.emit(event as any, data);
  }, [engine]);

  return { on, off, emit };
};

/**
 * Hook for accessing and updating flow variables
 * Use this for component-level variable management
 */
export const useFlowVariable = (key: string, defaultValue?: any) => {
  const { getVariable, setVariable } = useFlowContext();

  const value = getVariable(key) ?? defaultValue;

  const setValue = useCallback((newValue: any) => {
    setVariable(key, newValue);
  }, [key, setVariable]);

  return [value, setValue] as const;
};

/**
 * Hook for managing flow messages
 * Use this for components that need to display or add messages
 */
export const useFlowMessages = () => {
  const { messages, addMessage } = useFlowContext();

  const addUserMessage = useCallback((text: string, imageUrl?: string, fileName?: string, fileSize?: number) => {
    addMessage({
      from: 'user',
      text,
      imageUrl,
      fileName,
      fileSize,
    });
  }, [addMessage]);

  const addBotMessage = useCallback((text: string, stepId?: string, imageUrl?: string) => {
    addMessage({
      from: 'bot',
      text,
      stepId,
      imageUrl,
    });
  }, [addMessage]);

  const addSystemMessage = useCallback((text: string, stepId?: string) => {
    addMessage({
      from: 'bot',
      text,
      stepId,
      isSystem: true,
    });
  }, [addMessage]);

  const addLoadingMessage = useCallback(() => {
    addMessage({
      from: 'bot',
      text: '',
      isLoading: true,
      temporary: true,
    });
  }, [addMessage]);

  return {
    messages,
    addMessage,
    addUserMessage,
    addBotMessage,
    addSystemMessage,
    addLoadingMessage,
  };
};